import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';

import { Dropdown } from 'primereact/dropdown';

import '../../styles/login.scss';

import logo from '../../assets/images/TnLogo.png';
import illustration from '../../assets/images/loginDesign2.png';

import { useUI } from '../../shared/UI/Uicontext';
import { AppDispatch } from '../../store';
import { clearSession, loadSession } from '../../store/slices/sessionSlice';
import {
  clearStoredSession,
  decodeSessionFromStorage,
  storeSession,
} from '../../shared/services/userSessionService';
import { useEloginMutation, useForgotPasswordMutation } from './Login.service';

/**
 * Migrated from Angular `LoginComponent`
 * (src/app/views/pages/auth/login/login.component.{ts,html,scss}).
 *
 * The markup mirrors login.component.html class-for-class so the Bootstrap /
 * NobleUI / PrimeNG styling lands identically. The three template states
 * Angular toggled with `showLoginForm` / `showForgetForm` / `successHide` are
 * modelled here as a single `view` value.
 */
type LoginView = 'login' | 'forgot' | 'success';

/**
 * Angular has the "Forgot Password" trigger commented out in the template, so
 * the live page only shows the Login button. The flow below is fully migrated -
 * flip this (or pass the prop) to surface the entry point.
 */
const SHOW_FORGOT_PASSWORD = false;

const USER_TYPE = {
  school: '1',
  teacher: '2',
} as const;

const USER_TYPE_OPTIONS = [
  { label: 'School', value: USER_TYPE.school },
  { label: 'Teacher', value: USER_TYPE.teacher },
];

/** Angular: digitlength11 / digitlength8 patterns on the forgot-password form. */
const USERNAME_LENGTH: Record<string, number> = {
  [USER_TYPE.school]: 11,
  [USER_TYPE.teacher]: 8,
};

/**
 * Landing routes per user type, ported from Angular's `onLogin()`.
 *
 * Only the dashboard exists in this React app so far, so the not-yet-migrated
 * counselling screens point at it for now. Swap each value for its real path as
 * those pages land - the branching logic below already matches Angular.
 */
const LANDING_ROUTES = {
  /** Angular: /counselling/dashboard */
  dashboard: '/counselling/dashboard',
  /** Angular: /counselling/aided-sch-approval */
  aidedSchApproval: '/counselling/aided-sch-approval',
  /** Angular: /counselling/counseling-reset */
  counsellingReset: '/counselling/counseling-reset',
  /** Angular: /counselling/teacher-transfer */
  teacherTransfer: '/counselling/teacher-transfer',
  /** Angular: /counselling/plan-new-counselling */
  planNewCounselling: '/counselling/plan-new-counselling',
};

export function resolveLandingRoute(
  emisUserType?: string,
  emisUserType1?: string
): string {
  const userType = String(emisUserType ?? '');
  const userType1 = String(emisUserType1 ?? '');

  if (['9', '6', '10'].includes(userType)) {
    return LANDING_ROUTES.dashboard;
  }
  if (['30', '41', '63'].includes(userType)) {
    return LANDING_ROUTES.aidedSchApproval;
  }
  if (userType === '5' && userType1 === '5') {
    return LANDING_ROUTES.counsellingReset;
  }
  if (userType === '22' || (userType === '5' && userType1 === '7')) {
    return LANDING_ROUTES.teacherTransfer;
  }
  if (userType === '5' && userType1 === '6') {
    return LANDING_ROUTES.planNewCounselling;
  }
  return LANDING_ROUTES.dashboard;
}

/**
 * The Angular inputs carried an `avoidscripttags` attribute. That directive no
 * longer exists in the Angular repo, so this keeps its intent: never let markup
 * into the credential fields.
 */
const stripMarkup = (value: string): string => value.replace(/<[^>]*>?/g, '');

export interface LoginProps {
  /** Reveals the migrated forgot-password flow (hidden in Angular). */
  showForgotPassword?: boolean;
}

export default function Login({
  showForgotPassword = SHOW_FORGOT_PASSWORD,
}: LoginProps = {}) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { showLoader, hideLoader, showSuccess, showError } = useUI();

  const [view, setView] = useState<LoginView>('login');
  // Angular: IsShow - true means the value is masked.
  const [isMasked, setIsMasked] = useState(true);
  const [elogin] = useEloginMutation();
  const [forgotPassword] = useForgotPasswordMutation();

  // Angular's ngOnInit() called onLogout() so the login screen always starts
  // from a clean session.
  useEffect(() => {
    clearStoredSession();
    dispatch(clearSession());
  }, [dispatch]);

  const loginForm = useFormik({
    initialValues: { userName: '', password: '' },
    validate: (values) => {
      const errors: Partial<typeof values> = {};
      if (!values.userName.trim()) errors.userName = 'Username is required';
      if (!values.password.trim()) errors.password = 'Password is required';
      return errors;
    },
    onSubmit: async ({ userName, password }) => {
      showLoader();
      try {
        const response = await elogin({ userName, password }).unwrap();

        if (!response?.dataStatus || !response.records?.token) {
          // Angular: alertService.error(data.message)
          showError(response?.message || 'Unable to login. Please try again.');
          return;
        }

        localStorage.setItem('token', response.records.token);

        const session = decodeSessionFromStorage();
        if (session) {
          dispatch(loadSession(session));
          // Angular: authService.elogin() -> sessionService.create(sessionData)
          storeSession(session);
        }

        showSuccess('Login successful');
        navigate(
          resolveLandingRoute(session?.usertypeid, session?.emisUsertype1)
        );
      } catch {
        // dynamicBaseQuery already surfaces transport/HTTP errors via toast.
      } finally {
        hideLoader();
      }
    },
  });

  const forgotForm = useFormik({
    initialValues: { userType: '', userName: '' },
    validate: (values) => {
      const errors: Partial<typeof values> = {};

      if (!values.userType) {
        errors.userType = 'Usertype is required';
      }

      const length = USERNAME_LENGTH[values.userType];
      if (length) {
        const pattern = new RegExp(`^[0-9]{${length},${length}}$`);
        if (!pattern.test(values.userName.trim())) {
          errors.userName = `Invalid Pattern Limit ${length} digits`;
        }
      } else if (!values.userName.trim()) {
        errors.userName = 'Username is required';
      }

      return errors;
    },
    onSubmit: async ({ userType, userName }) => {
      showLoader();
      try {
        await forgotPassword({ userType, userName: userName.trim() }).unwrap();
        showSuccess('Successfully Submitted');
        setView('success');
      } catch {
        // Error toast is raised by dynamicBaseQuery.
      } finally {
        hideLoader();
      }
    },
  });

  /** Angular: userTypeChange() - swaps the username pattern and resets it. */
  const handleUserTypeChange = (value: string) => {
    forgotForm.setFieldValue('userType', value);
    forgotForm.setFieldValue('userName', '');
    forgotForm.setFieldTouched('userName', false, false);
  };

  const openForgotForm = () => {
    forgotForm.resetForm();
    setView('forgot');
  };

  /**
   * Angular's onLoginPage() did a `window.location.reload()`. Resetting state
   * gets to the same place without dropping the app bundle.
   */
  const backToLogin = () => {
    loginForm.resetForm();
    forgotForm.resetForm();
    setView('login');
  };

  /** Angular: eyeClick() */
  const toggleMask = () => setIsMasked((masked) => !masked);

  const loginError = (field: 'userName' | 'password') =>
    loginForm.touched[field] && loginForm.errors[field]
      ? loginForm.errors[field]
      : undefined;

  const forgotError = (field: 'userType' | 'userName') =>
    forgotForm.touched[field] && forgotForm.errors[field]
      ? forgotForm.errors[field]
      : undefined;

  // Angular: hideUserNM - the username block only appears once a type is picked.
  const userTypeSelected = Boolean(forgotForm.values.userType);

  return (
    <div className="login-page">
      <div className="row text-center">
        <div className="col-md-1 col-xl-2"></div>
        <div className="col-md-9 col-xl-7">
          <div className="card">
            <div className="row">
              <div className="col-md-6 pe-md-0 sideImg">
                <img src={illustration} alt="" />
              </div>

              <div className="col-md-6 ps-md-0">
                <div className="auth-form-wrapper px-4 mt-4">
                  <div className="tnLogo text-center">
                    <img src={logo} alt="Government of Tamil Nadu" />
                  </div>
                  <span className="nobleui-logo d-block mb-2">
                    School Education Department
                  </span>
                  <span>Education Management Information System</span>

                  {view === 'login' && (
                    <form
                      name="loginForm"
                      className="forms-sample"
                      onSubmit={loginForm.handleSubmit}
                      noValidate
                    >
                      <div className="mb-2 mt-2 text-start">
                        <label className="inMandatory" htmlFor="userName">
                          User Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="userName"
                          name="userName"
                          placeholder="Username"
                          autoComplete="off"
                          value={loginForm.values.userName}
                          onChange={(e) =>
                            loginForm.setFieldValue(
                              'userName',
                              stripMarkup(e.target.value)
                            )
                          }
                          onBlur={() =>
                            loginForm.setFieldTouched('userName', true)
                          }
                          aria-invalid={Boolean(loginError('userName'))}
                          aria-describedby="userName-error"
                        />
                        {loginError('userName') && (
                          <span
                            id="userName-error"
                            className="form-text text-danger"
                            role="alert"
                          >
                            {loginError('userName')}
                          </span>
                        )}
                      </div>

                      <div className="text-start">
                        <label
                          htmlFor="exampleInputPassword1"
                          className="inMandatory"
                        >
                          Password
                        </label>
                        <br />
                        <span className="p-input-icon-right">
                          <input
                            type={isMasked ? 'password' : 'text'}
                            className="form-control"
                            id="exampleInputPassword1"
                            name="password"
                            placeholder="Password"
                            autoComplete="current-password"
                            value={loginForm.values.password}
                            onChange={(e) =>
                              loginForm.setFieldValue(
                                'password',
                                stripMarkup(e.target.value)
                              )
                            }
                            onBlur={() =>
                              loginForm.setFieldTouched('password', true)
                            }
                            aria-invalid={Boolean(loginError('password'))}
                            aria-describedby="password-error"
                          />
                          {/*
                            Angular renders a bare clickable <i>. Kept as an
                            <i> so the PrimeReact `.p-input-icon-right > i`
                            positioning still applies, with keyboard support
                            added on top.
                          */}
                          <i
                            className={
                              isMasked ? 'pi pi-eye' : 'pi pi-eye-slash'
                            }
                            role="button"
                            tabIndex={0}
                            aria-label={
                              isMasked ? 'Show password' : 'Hide password'
                            }
                            aria-pressed={!isMasked}
                            onClick={toggleMask}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleMask();
                              }
                            }}
                          />
                        </span>
                        {loginError('password') && (
                          <span
                            id="password-error"
                            className="form-text text-danger"
                            role="alert"
                          >
                            {loginError('password')}
                          </span>
                        )}
                      </div>

                      <div className="text-center mt-4 testalign">
                        <button
                          type="submit"
                          className="btn btn-primary me-2 mb-2 mb-md-0"
                        >
                          Login
                        </button>
                        {showForgotPassword && (
                          <button
                            type="button"
                            className="btn btn-primary me-2 mb-2 mb-md-0"
                            onClick={openForgotForm}
                          >
                            Forgot Password
                          </button>
                        )}
                      </div>
                    </form>
                  )}

                  {/* {view === 'forgot' && (
                    <form
                      name="forgetForm"
                      className="forms-sample"
                      onSubmit={forgotForm.handleSubmit}
                      noValidate
                    >
                      <div className="form-group row mt-2 text-start">
                        <label className="inMandatory" htmlFor="userType">
                          Please Select Your User Type
                        </label>
                        <span className="p-input-icon-right">
                          <Dropdown
                            inputId="userType"
                            name="userType"
                            className="pselectbut"
                            panelClassName="dropdwnvalues"
                            placeholder="UserType"
                            options={USER_TYPE_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            value={forgotForm.values.userType}
                            onChange={(e) => handleUserTypeChange(e.value)}
                            onBlur={() =>
                              forgotForm.setFieldTouched('userType', true)
                            }
                            aria-describedby="userType-error"
                          />
                        </span>
                        {forgotError('userType') && (
                          <span
                            id="userType-error"
                            className="form-text text-danger"
                            role="alert"
                          >
                            {forgotError('userType')}
                          </span>
                        )}
                      </div>

                      {userTypeSelected && (
                        <>
                          <div className="mb-2 text-start">
                            <label
                              className="inMandatory"
                              htmlFor="forgotUserName"
                            >
                              User Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="forgotUserName"
                              name="userName"
                              placeholder="Username"
                              autoComplete="off"
                              inputMode="numeric"
                              maxLength={
                                USERNAME_LENGTH[forgotForm.values.userType]
                              }
                              value={forgotForm.values.userName}
                              onChange={(e) =>
                                forgotForm.setFieldValue(
                                  'userName',
                                  stripMarkup(e.target.value)
                                )
                              }
                              onBlur={() =>
                                forgotForm.setFieldTouched('userName', true)
                              }
                              aria-invalid={Boolean(forgotError('userName'))}
                              aria-describedby="forgotUserName-error"
                            />
                            {forgotError('userName') && (
                              <span
                                id="forgotUserName-error"
                                className="form-text text-danger"
                                role="alert"
                              >
                                {forgotError('userName')}
                              </span>
                            )}
                          </div>

                          <div className="mb-2 text-start">
                            <p>
                              Your Password will be sent to your registered
                              mail-Id when <br /> approved by your BRTE / Block
                              co-ordinator. Do you want to submit your
                              &quot;Forgot Password&quot; request?
                            </p>
                          </div>

                          <div className="text-center mt-2">
                            <button
                              type="submit"
                              className="btn btn-primary me-2 mb-2 mb-md-0"
                            >
                              Submit Request
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary me-2 mb-2 mb-md-0"
                              onClick={backToLogin}
                            >
                              Back to Login
                            </button>
                          </div>
                        </>
                      )}

                      {!userTypeSelected && (
                        <div className="text-center mt-2">
                          <button
                            type="button"
                            className="btn btn-primary me-2 mb-2 mb-md-0"
                            onClick={backToLogin}
                          >
                            Back to Login
                          </button>
                        </div>
                      )}
                    </form>
                  )} */}

                  {/* {view === 'success' && (
                    <div className="mb-8 mt-8 text-start">
                      <p>
                        Your request has been sent successfully pleace contact{' '}
                        <br /> your BRTE / Block co-ordinator and to receive the
                        password
                      </p>
                      <h6 className="text-center">Thank You</h6>
                      <div className="text-center mt-2">
                        <button
                          type="button"
                          className="btn btn-primary me-2 mb-2 mb-md-0"
                          onClick={backToLogin}
                        >
                          Back to Login
                        </button>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
